import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, CircleX, Monitor } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Main } from '../../components'
import Card from '../../components/Card'
import LoginButtons from '../../components/LoginButtons'
import { usePageInfo } from '../../config/queries'
import { authorizeDesktopClient } from '../../api/user'
import { displayErrorToast } from '../../helpers'

const REDIRECT_URI_FORMAT = /^http:\/\/127\.0\.0\.1:\d{1,5}\/callback$/

const ResultMessage = ({ success }: { success: boolean }) => (
  <div className='flex flex-col items-center gap-3 text-center'>
    {success ? (
      <CheckCircle2 size={40} className='text-primary' />
    ) : (
      <CircleX size={40} className='text-destructive' />
    )}
    <h3>
      {success ? 'Desktop client connected' : 'Desktop client not connected'}
    </h3>
    <p className='text-muted-foreground'>
      {success
        ? 'You can close this tab and return to the wowaudit desktop client.'
        : 'Something went wrong or the login was cancelled. You can close this tab and try again from the desktop client.'}
    </p>
  </div>
)

const DesktopAuthorize = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const {
    data: { user },
  } = usePageInfo()
  const [submitting, setSubmitting] = useState(false)

  const result = searchParams.get('result')
  const codeChallenge = searchParams.get('code_challenge') ?? ''
  const state = searchParams.get('state') ?? ''
  const redirectUri = searchParams.get('redirect_uri') ?? ''
  const validRequest =
    REDIRECT_URI_FORMAT.test(redirectUri) && !!codeChallenge && !!state

  const authorize = () => {
    setSubmitting(true)
    authorizeDesktopClient({ codeChallenge, redirectUri, state })
      .then(({ redirect_url }) => window.location.assign(redirect_url))
      .catch((err) => {
        displayErrorToast(err, t)
        setSubmitting(false)
      })
  }

  const cancel = () => {
    const params = new URLSearchParams({ error: 'access_denied', state })
    window.location.assign(`${redirectUri}?${params.toString()}`)
  }

  return (
    <Main title='Desktop client login' robots='noindex, nofollow'>
      <div className='max-w-md mx-auto mt-16 px-4'>
        <Card size='lg' className='flex flex-col gap-6'>
          {result ? (
            <ResultMessage success={result === 'success'} />
          ) : !validRequest ? (
            <div className='text-center'>
              <h3>Invalid login link</h3>
              <p className='text-muted-foreground'>
                Please start the login again from the wowaudit desktop client.
              </p>
            </div>
          ) : (
            <>
              <div className='flex flex-col items-center gap-3 text-center'>
                <Monitor size={40} className='text-primary' />
                <h3 className='mb-0'>Log in to the desktop client</h3>
                <p className='text-muted-foreground mb-0'>
                  {user
                    ? `The wowaudit desktop client will sync in-game data for your guild and characters to wowaudit as ${user.battletag}.`
                    : 'Log in to wowaudit to connect the desktop client to your account.'}
                </p>
              </div>

              {user ? (
                <div className='flex flex-col gap-2'>
                  <Button onClick={authorize} loading={submitting}>
                    Authorize desktop client
                  </Button>
                  <Button
                    variant='outline'
                    onClick={cancel}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <p className='text-xs text-muted-foreground text-center mt-2 mb-0'>
                    You can revoke access at any time in your profile settings.
                  </p>
                </div>
              ) : (
                <LoginButtons
                  withLabel={false}
                  inSidebar
                  grow
                  origin={`${window.location.pathname}${window.location.search}`}
                />
              )}
            </>
          )}
        </Card>
      </div>
    </Main>
  )
}

export default DesktopAuthorize
